import type { PortfolioActivityMode } from '../events'

import { trackAnalyticsEvent } from '../track'

/**
 * Fired once per session when the Portfolio nav tab is rendered. Provides the
 * impression denominator for the click event (the discoverability funnel behind
 * VAU-1134: how many sessions saw the tab vs. clicked it). Intentionally carries
 * no position count — the count isn't settled at first render, so it would be a
 * misleading ~0; the click event carries the (settled) count instead.
 */
export function trackPortfolioNavTabViewed() {
  trackAnalyticsEvent('portfolio_nav_tab_viewed', {})
}

/**
 * Fired when the user selects the Portfolio tab from the main navigation.
 * `position_count` lets us measure whether users with positions are the ones
 * discovering the tab (the discoverability goal behind VAU-1134).
 *
 * `positionCount` is `undefined` when the count isn't a settled total
 * (disconnected, still loading, or a failed positions query). In that case we
 * omit `position_count` rather than log 0, which would conflate "unknown" with
 * "genuinely holds nothing" and undercount position-holders in the metric.
 */
export function trackPortfolioNavTabClicked(params: {
  source: 'navbar'
  positionCount: number | undefined
}) {
  trackAnalyticsEvent('portfolio_nav_tab_clicked', {
    source: params.source,
    ...(params.positionCount !== undefined && { position_count: params.positionCount })
  })
}

/**
 * Fired once per Portfolio visit when the "Lend positions" table renders with
 * at least one position (VRM-5331). The section is hidden for accounts without
 * direct lend positions, so this impression measures how many portfolio views
 * actually surface the table — the adoption denominator for the upcoming
 * withdraw flow (VRM-5326).
 */
export function trackPortfolioLendPositionsViewed(params: { positionCount: number }) {
  trackAnalyticsEvent('portfolio_lend_positions_viewed', {
    position_count: params.positionCount
  })
}

/**
 * Fired when a lend-position row (desktop) or card (mobile) is opened toward
 * its market page — the click numerator of the
 * `portfolio_lend_positions_viewed` impression funnel.
 */
export function trackPortfolioLendPositionClicked(params: { chainId: number; marketId: string }) {
  trackAnalyticsEvent('portfolio_lend_position_clicked', {
    chain_id: params.chainId,
    market_id: params.marketId
  })
}

/**
 * Fired when a user switches the portfolio Activity section between Vaults
 * (`earn`), Markets (`borrow`) and Liquidation history. Only fires when the mode
 * changes, so `activity_mode_changed` broken down by `mode` shows which history
 * users open.
 */
export function trackActivityModeChanged({ mode }: { mode: PortfolioActivityMode }) {
  trackAnalyticsEvent('activity_mode_changed', { page: 'portfolio', mode })
}
